import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const EmployerOnboardingGuide = ({ userProfile }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [showGuide, setShowGuide] = useState(false);
  // Use user-specific localStorage key to prevent data leaking between users
  const storageKey = userProfile?.id ? `employer_completed_onboarding_${userProfile.id}` : 'employer_completed_onboarding';
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useLocalStorage(storageKey, false);
  
  // Check if user is new or needs onboarding
  useEffect(() => {
    // Don't show guide if user has completed onboarding
    if (hasCompletedOnboarding) return;
    
    // Don't proceed if userProfile is not loaded yet
    if (!userProfile) return;

    // Skip modal if user just came from the onboarding page
    const recentlyStartedProfile = localStorage.getItem(`employer_started_profile_${userProfile.id}`);
    if (recentlyStartedProfile) {
      setHasCompletedOnboarding(true);
      return;
    }
    
    // Check if this is a new employer (created within last 7 days)
    const isNewUser = userProfile?.created_at ? 
      (new Date() - new Date(userProfile.created_at) < 7 * 24 * 60 * 60 * 1000) : false;
    
    // Calculate profile completion percentage based on filled fields
    let filledFields = 0;
    let totalFields = 0;
    
    const profileFields = [
      'full_name',
      'company_name',
      'bio',
      'phone',
      'location',
      'website',
      'linkedin',
      'company_size',
      'industry',
      'profile_picture'
    ];
    
    profileFields.forEach(field => {
      totalFields++;
      const value = userProfile[field];
      if (value !== null && value !== undefined && value !== '' && 
          (!Array.isArray(value) || value.length > 0)) {
        filledFields++;
      }
    });
    
    const profileCompletion = (filledFields / totalFields) * 100;
    
    // Don't show guide if profile is 100% complete
    if (profileCompletion === 100) {
      setHasCompletedOnboarding(true);
      return;
    }
    
    // Show guide for new employers or users with incomplete profiles (< 60%)
    if (isNewUser || profileCompletion < 60) {
      setShowGuide(true);
    }
  }, [userProfile, hasCompletedOnboarding]);
  
  // Define onboarding steps for employers
  const steps = [
    {
      title: 'Welcome to Your Employer Dashboard!',
      content: 'This is your central hub for managing job postings, tracking applications, and connecting with Virtual Assistants. Let\'s get you started!',
      icon: 'Home',
      target: 'welcome-section',
      action: null
    },
    {
      title: 'Complete Your Company Profile',
      content: 'A complete company profile attracts better candidates! Add your company details, industry, size, and professional information to stand out.',
      icon: 'User',
      target: 'profile-completion',
      action: () => navigate('/employer-profile-settings')
    },
    {
      title: 'Post Your First Job',
      content: 'Create job postings to find the perfect Virtual Assistants. Define requirements, experience level, skills needed, and compensation to attract qualified candidates.',
      icon: 'FileText',
      target: null,
      action: () => navigate('/job-posting')
    },
    {
      title: 'Browse Qualified VAs',
      content: 'Search through profiles of skilled Virtual Assistants. Filter by skills, experience, and hourly rates to find the right match for your needs.',
      icon: 'Search',
      target: null,
      action: () => navigate('/candidate-search')
    },
    {
      title: 'Manage Applications',
      content: 'Track all applications to your job postings. Review candidate profiles, shortlist applicants, and move them through your hiring pipeline efficiently.',
      icon: 'BarChart',
      target: null,
      action: () => navigate('/employer-dashboard?tab=applications')
    },
    {
      title: 'Schedule Interviews',
      content: 'Set up interviews with promising candidates. Coordinate schedules and keep track of all upcoming interviews in one organized place.',
      icon: 'Calendar',
      target: null,
      action: () => navigate('/interview-management')
    },
    {
      title: 'Connect & Communicate',
      content: 'Use our secure messaging system to communicate directly with VAs. Answer questions, provide feedback, and build strong working relationships.',
      icon: 'MessageCircle',
      target: null,
      action: () => navigate('/messages')
    },
    {
      title: 'Manage Your Team',
      content: 'Once you\'ve hired VAs, manage your team effectively. Track performance, provide feedback, and nurture long-term professional relationships.',
      icon: 'Award',
      target: null,
      action: () => navigate('/employer-dashboard?tab=hired')
    }
  ];

  // Handle next/previous step
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      const nextStep = steps[currentStep + 1];

      // If the step has an explicit navigation action, run it (takes precedence)
      if (nextStep.action) {
        nextStep.action();
        return;
      }

      // Otherwise, if the step targets an on-page section, try to scroll to it.
      if (nextStep.target) {
        // If the element exists on the current page, scroll to it.
        const attemptScroll = () => {
          const targetElement = document.getElementById(nextStep.target);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return true;
          }
          return false;
        };

        if (!attemptScroll()) {
          // If not present (user navigated away), navigate to the dashboard then scroll after a short delay
          navigate('/employer-dashboard');
          setTimeout(() => {
            attemptScroll();
          }, 250);
        }
      }
    } else {
      // Complete onboarding
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      const prevStep = steps[currentStep - 1];

      // If previous step has an explicit navigation action, run it
      if (prevStep.action) {
        prevStep.action();
        return;
      }

      // Otherwise, try to scroll to the target section (navigating to dashboard if necessary)
      if (prevStep.target) {
        const attemptScroll = () => {
          const targetElement = document.getElementById(prevStep.target);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return true;
          }
          return false;
        };

        if (!attemptScroll()) {
          navigate('/employer-dashboard');
          setTimeout(() => {
            attemptScroll();
          }, 250);
        }
      }
    }
  };

  // Mark onboarding as complete
  const handleComplete = () => {
    setHasCompletedOnboarding(true);
    setShowGuide(false);
  };

  // Skip onboarding
  const handleSkip = () => {
    setHasCompletedOnboarding(true);
    setShowGuide(false);
  };

  // Expose reset function for debugging/testing (dev only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.resetEmployerOnboarding = () => {
        setHasCompletedOnboarding(false);
        setShowGuide(true);
        setCurrentStep(0);
        console.log('Employer onboarding reset - modal should show now');
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.resetEmployerOnboarding;
      }
    };
  }, [setHasCompletedOnboarding]);

  if (!showGuide) return null;

  return (
    <div className="fixed bottom-20 md:bottom-8 left-0 right-0 z-50 flex justify-center px-4">
      <div className="bg-white rounded-lg shadow-xl border border-primary-200 w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-accent px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Icon name={steps[currentStep].icon} size={20} className="text-white" />
            {steps[currentStep].title}
          </h3>
          <button 
            onClick={handleSkip} 
            className="text-white hover:text-white/80 transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        
        <div className="p-6">
          <p className="mb-6 text-text-primary">{steps[currentStep].content}</p>
          
          {/* Action Button for Steps with Navigation */}
          {/* {steps[currentStep].action && (
            <div className="mb-6">
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={steps[currentStep].action}
                iconName="ArrowUpRight"
                iconPosition="right"
                className="w-full"
              >
                Go to {steps[currentStep].title.split(' ').slice(0, 2).join(' ')}
              </Button>
            </div>
          )} */}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {steps.map((_, index) => (
                <div 
                  key={index} 
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep ? 'w-6 bg-primary' : 'w-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePrevious}
                  iconName="ArrowLeft"
                >
                  Back
                </Button>
              )}
              
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleNext}
                iconName={currentStep < steps.length - 1 ? "ArrowRight" : "Check"}
                iconPosition={currentStep < steps.length - 1 ? "right" : "left"}
              >
                {currentStep < steps.length - 1 ? 'Next' : 'Got It!'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerOnboardingGuide;
